"use client";
import { useState, useEffect, useRef } from "react";
import { OverlayPanel } from "primereact/overlaypanel";
import { InputText } from "primereact/inputtext";
import { useRouter, usePathname } from "next/navigation";
import { ShippingEstimate } from "./shipping/ShippingEstimate";
import { useShipping } from "./shipping/useShipping";
import { groupCategories } from "@/_config";
import { InlineSearch } from "./search/InlineSearch";

const VISIBLE_COUNT = 5; // pills shown inline before "+ More"

const CATEGORY_ICONS = {
  all:                    "pi pi-th-large",
  tripods:                "pi pi-camera",
  microphone:             "pi pi-microphone",
  flash:                  "pi pi-sun",
  "mobile-accessories":   "pi pi-mobile",
  game:                   "pi pi-play",
  "printers-scanners":    "pi pi-print",
  "keyboards-mice":       "pi pi-desktop",
  "webcams-conferencing": "pi pi-video",
  "ups-power-backup":     "pi pi-bolt",
  "networking-devices":   "pi pi-wifi",
  sensors:                "pi pi-shield",
  "smart-home":           "pi pi-home",
  default:                "pi pi-box",
};

// Deterministic color per category — same slug always gets the same swatch,
// new/unknown slugs still get a stable color instead of falling back to grey.
const CATEGORY_COLORS = [
  { primary: "#3b82f6", light: "#dbeafe" }, // blue
  { primary: "#1bb8a7", light: "#ccfbf1" }, // teal
  { primary: "#6366f1", light: "#e0e7ff" }, // indigo
  { primary: "#10b981", light: "#d1fae5" }, // emerald
  { primary: "#f59e0b", light: "#fef3c7" }, // amber
  { primary: "#8b5cf6", light: "#ede9fe" }, // violet
  { primary: "#0ea5e9", light: "#e0f2fe" }, // sky
  { primary: "#059669", light: "#d1fae5" }, // green
];
const ALL_COLOR = { primary: "#0b3163", light: "#e5edf7" };

const ALL = { _id: "all", title: "All", slug: "all", isActive: true };

const icon = (slug) =>
  CATEGORY_ICONS[slug?.toLowerCase()] ?? CATEGORY_ICONS.default;

const colorFor = (slug) => {
  if (!slug || slug === "all") return ALL_COLOR;
  let h = 0;
  for (let i = 0; i < slug.length; i++) h = (h * 31 + slug.charCodeAt(i)) >>> 0;
  return CATEGORY_COLORS[h % CATEGORY_COLORS.length];
};


// ── Pill button ───────────────────────────────────────────────────────────────
const Pill = ({ category, active, onClick }) => (
  <button
    type="button"
    onClick={() => onClick(category.slug)}
    aria-pressed={active}
    style={{
      display:        "inline-flex",
      alignItems:     "center",
      gap:            "0.4rem",
      padding:        "0.4rem 1rem",
      borderRadius:   "20px",
      border:         active ? "1.5px solid var(--primary-color)" : "1.5px solid #e2e8f0",
      background:     active ? "var(--primary-color)" : "white",
      color:          active ? "#ffffff" : "#475569",
      fontSize:       "0.85rem",
      cursor:         "pointer",
      whiteSpace:     "nowrap",
      transition:     "all 0.18s ease",
      fontWeight:     active ? 600 : 400,
      WebkitAppearance: "none",
      appearance:     "none",
      outline:        "none",
    }}
  >
    <i className={icon(category.slug)} style={{ fontSize: "0.8rem" }} />
    {category.title}
  </button>
);

// ── Overlay category card ─────────────────────────────────────────────────────
const OverlayCard = ({ category, active, onClick }) => (
  <button
    type="button"
    onClick={() => onClick(category.slug)}
    style={{
      display:       "flex",
      alignItems:    "center",
      gap:           "0.5rem",
      padding:       "0.55rem 0.85rem",
      borderRadius:  "10px",
      border:        active ? "1.5px solid #0b3163" : "1.5px solid #e2e8f0",
      background:    active ? "rgba(11,49,99,0.12)" : "#ffffff",
      color:         active ? "#0b3163" : "#475569",
      fontSize:      "0.82rem",
      cursor:        "pointer",
      width:         "100%",
      textAlign:     "left",
      transition:    "all 0.15s ease",
      fontWeight:    active ? 600 : 400,
      WebkitAppearance: "none",
      appearance:    "none",
      outline:       "none",
    }}
  >
    <i className={icon(category.slug)} style={{ fontSize: "0.9rem", minWidth: 16 }} />
    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
      {category.title}
    </span>
    {active && (
      <i className="pi pi-check" style={{ marginLeft: "auto", fontSize: "0.75rem", color: "#6366f1" }} />
    )}
  </button>
);

// ── Flipkart-style category tile (mobile / tablet) ────────────────────────────
const CategoryTile = ({ category, active, compact, onClick }) => {
  const c = colorFor(category.slug);
  return (
    <button
      type="button"
      data-cat-slug={category.slug}
      onClick={() => onClick(category.slug)}
      aria-pressed={active}
      style={{
        display:          "flex",
        flexDirection:    "column",
        alignItems:       "center",
        gap:              "0.15rem",
        width:            compact ? "auto" : "100%",
        minWidth:         0,
        background:       "none",
        border:           "none",
        padding:          compact ? "0.3rem 0.5rem" : "0.15rem 0 0",
        cursor:           "pointer",
        WebkitAppearance: "none",
        appearance:       "none",
        outline:          "none",
      }}
    >
      {!compact && (
        <span
          style={{
            width:          "clamp(40px, 12vw, 50px)",
            borderRadius:   "50%",
            display:        "flex",
            alignItems:     "center",
            justifyContent: "center",
            background:     "transparent",
            border:         "none",
            transition:     "color 0.18s ease",
          }}
        >
          <i
            className={icon(category.slug)}
            style={{ fontSize: "1.25rem", color: active ? c.primary : "#475569" }}
          />
        </span>
      )}
      <span
        style={{
          fontSize:     compact ? "0.78rem" : "0.7rem",
          fontWeight:   active ? 600 : 500,
          color:        active ? c.primary : "#475569",
          textAlign:    "center",
          whiteSpace:   "nowrap",
          overflow:     "hidden",
          textOverflow: "ellipsis",
          maxWidth:     "100%",
          lineHeight:   1.2,
        }}
      >
        {category.title}
      </span>
    </button>
  );
};

// ── Main content ──────────────────────────────────────────────────────────────
// Both the mobile tile-row and the desktop pill-row are always rendered; CSS
// (not JS) decides which one is visible. This keeps server and client markup
// identical on first paint — no hydration mismatch, so no loading skeleton
// is needed while the correct layout "figures itself out".
const buildCategories = (data) =>
  groupCategories(Array.isArray(data) && data.length ? [ALL, ...data] : [ALL]);

export const CategoryList = ({ data = [] }) => {
  const [categories, setCategories]   = useState(() => buildCategories(data));
  const [selected, setSelected]       = useState(ALL);
  const [search, setSearch]           = useState("");
  const overlayRef                    = useRef(null);
  const stripRef                      = useRef(null);
  const wrapRef                       = useRef(null);
  const barRef                        = useRef(null);
  const stuckRef                      = useRef(false);
  const [stuck, setStuck]             = useState(false);
  const [fixedTop, setFixedTop]       = useState(0);
  const [wrapH, setWrapH]             = useState(undefined);
  const router   = useRouter();
  const pathname = usePathname();
  const shipping = useShipping();

  useEffect(() => {
    setCategories(buildCategories(data));
  }, [data]);

  useEffect(() => {
    const strip = stripRef.current;
    const tile  = strip?.querySelector(`[data-cat-slug="${selected?.slug}"]`);
    if (!strip || !tile) return;
    const offset = tile.getBoundingClientRect().left - strip.getBoundingClientRect().left;
    strip.scrollTo({ left: strip.scrollLeft + offset - (strip.clientWidth - tile.offsetWidth) / 2, behavior: "smooth" });
  }, [selected]);

  useEffect(() => {
    const update = () => {
      const wrap = wrapRef.current;
      const bar  = barRef.current;
      if (!wrap || !bar || !window.matchMedia("(max-width: 960px)").matches) {
        stuckRef.current = false;
        setStuck(false);
        return;
      }
      const shouldStick = wrap.getBoundingClientRect().top <= 0;
      if (!shouldStick && !stuckRef.current) setWrapH(bar.offsetHeight);
      stuckRef.current = shouldStick;
      setFixedTop(0);
      setStuck(shouldStick);
    };
    const scroller = document.getElementById("layoutContainer");
    scroller?.addEventListener("scroll", update, { passive: true });
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    update();
    return () => {
      scroller?.removeEventListener("scroll", update);
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  useEffect(() => {
    const slug = pathname === "/" ? "all" : pathname.replace(/^\//, "");
    const match = categories.find(
      (c) => c.slug === slug || c.memberSlugs?.includes(slug)
    );
    setSelected(match ?? ALL);
  }, [pathname, categories]);

  const navigate = (slug) => {
    setSelected(categories.find((c) => c.slug === slug) ?? ALL); // instant highlight
    router.push(slug === "all" ? "/" : `/${slug}`);
    overlayRef.current?.hide();
    setSearch("");
  };

  const visible  = categories.slice(0, VISIBLE_COUNT);
  const overflow = categories.slice(VISIBLE_COUNT);          // shown in overlay
  const filtered = search.trim()
    ? categories.filter((c) =>
        c.title.toLowerCase().includes(search.toLowerCase())
      )
    : categories;

  const activeInOverflow = overflow.some((c) => c.slug === selected?.slug);

  return (
    <div>
      {/* ── Mobile / tablet: search + Flipkart-style row; fixes to the top on scroll ── */}
      <div ref={wrapRef} className="ennix-cat-mobile-wrap" style={{ minHeight: stuck ? wrapH : undefined }}>
      <div
        ref={barRef}
        className="ennix-cat-mobile-bar"
        style={stuck ? {
          position:   "fixed",
          top:        fixedTop,
          left:       0,
          right:      0,
          zIndex:     1000,
          background: "#ffffff",
          boxShadow:  "0 4px 14px rgba(0,0,0,0.08)",
          padding:    "0.35rem 0.6rem 0",
        } : undefined}
      >
      <div className="ennix-cat-mobile-search">
        <InlineSearch />
      </div>
      <div
        className="ennix-cat-mobile"
        ref={stripRef}
        style={{
          gridAutoFlow:       "column",
          gridAutoColumns:    stuck ? "max-content" : "minmax(68px, 1fr)",
          gap:                stuck ? "0.4rem" : "0.5rem",
          maxWidth:           "100%",
          minWidth:           0,
          overflowX:          "auto",
          padding:            "0.3rem 0.15rem 0.6rem",
          scrollbarWidth:     "none",
          msOverflowStyle:    "none",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {categories.map((cat) => (
          <CategoryTile
            key={cat.slug}
            category={cat}
            active={selected?.slug === cat.slug}
            compact={stuck}
            onClick={navigate}
          />
        ))}
      </div>
      </div>
      </div>

      {/* ── Desktop: pills + overlay + delivery check ─────────────────────── */}
      <div className="ennix-cat-desktop mb-1 align-items-center justify-content-between gap-3">
        {/* Visible pills */}
        <div className="flex gap-2 align-items-center flex-nowrap ">
          {visible.map((cat) => (
            <Pill
              key={cat.slug}
              category={cat}
              active={selected?.slug === cat.slug}
              onClick={navigate}
            />
          ))}

          {/* "+ More" button — only shown when there are extra categories */}
          {overflow.length > 0 && (
            <>
              <button
                onClick={(e) => { setSearch(""); overlayRef.current?.toggle(e); }}
                style={{
                  display:          "inline-flex",
                  alignItems:       "center",
                  gap:              "0.35rem",
                  padding:          "0.4rem 0.9rem",
                  borderRadius:     "20px",
                  border:           activeInOverflow ? "1.5px solid var(--primary-color)" : "1.5px solid #e2e8f0",
                  background:       activeInOverflow ? "var(--primary-color)" : "#ffffff",
                  color:            activeInOverflow ? "#ffffff" : "#475569",
                  fontSize:         "0.85rem",
                  fontWeight:       activeInOverflow ? 600 : 400,
                  cursor:           "pointer",
                  whiteSpace:       "nowrap",
                  transition:       "all 0.18s ease",
                  WebkitAppearance: "none",
                  appearance:       "none",
                  outline:          "none",
                }}
              >
                <i className="pi pi-th-large" style={{ fontSize: "0.8rem" }} />
                {activeInOverflow
                  ? `${selected?.title} ▾`
                  : `+${overflow.length} More ▾`}
              </button>

              {/* ── Overlay Panel ──────────────────────────────────────────── */}
              <OverlayPanel
                ref={overlayRef}
                style={{
                  width:        "340px",
                  background:   "#f8faff",
                  border:       "1px solid rgba(11,49,99,0.15)",
                  borderRadius: "14px",
                  padding:      "0",
                  boxShadow:    "0 8px 32px rgba(0,0,0,0.5)",
                }}
                pt={{ content: { style: { padding: 0 } } }}
              >
                {/* Search */}
                <div style={{ padding: "0.85rem 1rem 0.6rem" }}>
                  <span className="p-input-icon-left w-full">
                    <i className="pi pi-search" style={{ color: "#666", fontSize: "0.85rem", marginLeft: 3 }} />
                    <InputText
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search categories…"
                      autoFocus
                      style={{
                        width:        "100%",
                        background:   "#ffffff",
                        border:       "1px solid rgba(11,49,99,0.15)",
                        color:        "#ddd",
                        borderRadius: "8px",
                        padding:      "0.5rem 0.75rem 0.5rem 2rem",
                        fontSize:     "0.85rem",
                      }}
                    />
                  </span>
                </div>

                {/* Category grid */}
                <div
                  style={{
                    display:         "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap:             "0.4rem",
                    padding:         "0 1rem 1rem",
                    maxHeight:       "300px",
                    overflowY:       "auto",
                    overflowX:       "hidden",
                  }}
                >
                  {filtered.length === 0 ? (
                    <p style={{ color: "#666", fontSize: "0.8rem", gridColumn: "span 2", textAlign: "center", padding: "1rem 0" }}>
                      No categories found
                    </p>
                  ) : (
                    filtered.map((cat) => (
                      <OverlayCard
                        key={cat.slug}
                        category={cat}
                        active={selected?.slug === cat.slug}
                        onClick={navigate}
                      />
                    ))
                  )}
                </div>
              </OverlayPanel>
            </>
          )}
        </div>

        <ShippingEstimate shipping={shipping} />
      </div>
    </div>
  );
};
